import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, pairwise } from 'rxjs/operators';
import { SearchService } from '../../services/search.service';
import { CalAngularService } from '@cvx/cal-angular';
import { throwError } from 'rxjs';
import { AuthStateSignalsService } from '../../services/auth-state-signals.service';
import { AuthService } from '../../services/auth.service';
import { Roles } from '../../features/report-form/constants';
import { SessionAuditService } from '../../services/session-audit/session-audit.service';

interface User {
  name: string;
  email?: string;
}

@Component({
  selector: 'app-side-nav',
  standalone: false,
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent implements OnInit {
  @Output() sidebarToggled = new EventEmitter<boolean>();
  currentUserEmail: string | null = null;
  currentSource?: string;
  collapsed = false;
  currentUser: User | null = null;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sessionAudit = inject(SessionAuditService);

  toggleSidebar() {
    this.collapsed = !this.collapsed;
    this.sidebarToggled.emit(this.collapsed);
  }

  constructor(private router: Router, private searchService: SearchService,public auth: AuthStateSignalsService, private calService: CalAngularService, private authService: AuthService) {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      pairwise()
    ).subscribe((events: [NavigationEnd, NavigationEnd]) => {
      const previousUrl = events[0].urlAfterRedirects;
      const currentUrl = events[1].urlAfterRedirects;

      const previousFeature = this.getFeatureFromUrl(previousUrl);
      const currentFeature = this.getFeatureFromUrl(currentUrl);

      // Features that should persist their search across navigation
      const persistentSearchFeatures = ['forecasts', 'forecast-revisions'];

      if (previousFeature && previousFeature !== currentFeature) {
        if (!persistentSearchFeatures.includes(previousFeature)) {
          this.searchService.clearSearchTerm(previousFeature);
        }
      }
      this.cdr.markForCheck();
    });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        const tree = this.router.parseUrl(this.router.url);
        this.currentSource = tree.queryParams['source'] ?? undefined;
        this.cdr.markForCheck();
      });

  }

  ngOnInit(): void {

    this.calService.getClaims().then(user => {
      this.currentUser = { name: user.name ?? '', email: user.email }
    }
    ).catch(err => console.error(err));

  }

  isActiveBySource(name: string): boolean {
    if(!this.currentSource || this.currentSource.length === 0){
      const currentUrl = this.router.url;
      const segment = currentUrl.split('/').filter((segment) => segment != '');
      return segment[0] === name;
    }
    return (this.currentSource ?? '').toLowerCase() === name.toLowerCase();
  }

  getUserInitials(): string {
    if (!this.currentUser) return '';
    const names = this.currentUser.name.split(',').map(name => name.trim());
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return this.currentUser.name.slice(0, 2).toUpperCase();
  }

  openSettings(): void {
    this.router.navigate(['/settings']);
  }

  openHelp(): void {
    // console.log('Help clicked');
  }

  private getFeatureFromUrl(url: string): string {
    const parts = url.split('/');
    if (parts.length > 1) {
      return parts[1];
    }
    return '';
  }
   login() {
    this.auth.signIn().subscribe();
  }

  logout() {
    // `finalize` runs on success and on error, so sign-out can never be skipped,
    // and the audit call is never torn down by the MSAL redirect.
    this.sessionAudit
      .endSession()
      .pipe(finalize(() => this.auth.signOut().subscribe()))
      .subscribe();
  }

  canViewDashboard(): boolean {
    return this.authService.isInRole(Roles.PowerUser)
    // ||
    //        this.authService.isInRole(Roles.BreakInUserFull) ||
    //        this.authService.isInRole(Roles.BreakInUserLimited) ||
    //        this.authService.isInRole(Roles.Management) ||
    //        this.authService.isInRole(Roles.CostAnalytics)
           ;
  }

}
